import { useCallback, useMemo, useRef, useState } from 'react';
import { usePrevious } from 'react-use';

import isEqual from 'lodash/isEqual';

import { flushSync } from 'react-dom';
import { useOnBeforeUnload } from '../../../utils/hooks';

import { useRobotSelector } from '../context';
import { ROBOT_RUN_STATUS } from '../robot.constants';
import { ObjectAnswer } from '../robot.types';
import { getIsReadOnly } from '../selectors';

export function useUncontrolledCodeInput() {
    const [code, setCode] = useRobotSelector((s) => [s.code, s.setCode]);

    const [shouldConfirmExit, setShouldConfirmExit] = useState(false);
    useOnBeforeUnload(shouldConfirmExit);

    const keyRef = useRef(0);
    const codeRef = useRef<ObjectAnswer>();

    const handleChange: typeof setCode = useCallback(
        (newCode) => {
            codeRef.current = newCode;
            flushSync(() => {
                setCode(newCode);
            });
        },
        [setCode]
    );

    const prevCode = usePrevious(code);
    if (!isEqual(code, prevCode) && !isEqual(code, codeRef.current)) {
        keyRef.current += 1;
        codeRef.current = code;

        // При необходимости запросим подтверждение закрытия вкладки
        setShouldConfirmExit(true);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const initialCode = useMemo(() => code, [keyRef.current]);

    return [handleChange, initialCode, keyRef.current] as const;
}

export function useErrorLineNumber() {
    // Робот закончил воспроизведение кода
    const isFinished = useRobotSelector((s) => s.state?.status === ROBOT_RUN_STATUS.FINISH);
    const sample = useRobotSelector((s) => s.sample);
    const code = useRobotSelector((s) => s.code);

    // Результаты воспроизведения кода
    const results = useRobotSelector((s) => s.state?.results);
    const resultsCode = results?.user_answer;
    const resultsSample = results?.result?.[sample];
    const resultsError = resultsSample?.check_details.errors;
    const isSameAsResults = resultsCode === code;

    // Результаты после отправки ответа
    const attempt = useRobotSelector((s) => s.attempt);
    const attemptCode = attempt?.markers?.user_answer;
    const attemptSample = attempt?.markers?.result?.[sample];
    const attemptError = attemptSample?.check_details.errors;
    const isSameAsAttempt = attemptCode === code;

    // Подсвечиваем ошибку, если код тот же что и при отправке ответа или воспроизведении
    const errors = (isFinished && isSameAsResults && resultsError) || (isSameAsAttempt && attemptError) || undefined;

    return errors?.exception_line;
}

export function useReadOnly() {
    const READONLY_CODEMIRROR_NOCOPY = 'nocursor';

    const isPlayingAnswer = useRobotSelector((s) =>
        Boolean(s.state?.status !== ROBOT_RUN_STATUS.IDLE && READONLY_CODEMIRROR_NOCOPY)
    );
    const readOnly = useRobotSelector(getIsReadOnly);

    return readOnly || isPlayingAnswer;
}
